import { EmailData, Place } from '../types/results.types';

/**
 * Check if any email in the array is from a catch-all domain
 */
export const hasCatchAllEmails = (emails: EmailData[]): boolean => {
  if (!emails || emails.length === 0) return false;
  return emails.some(email => email.is_catch_all === true);
};

const normalizeDomain = (domain: string): string => (
  domain.trim().toLowerCase().replace(/^www\./, '').replace(/\.$/, '')
);

const getWebsiteDomain = (website?: string): string | null => {
  if (!website) return null;

  try {
    const url = new URL(website.includes('://') ? website : `https://${website}`);
    return normalizeDomain(url.hostname);
  } catch {
    return null;
  }
};

const getEmailDomain = (email?: string): string | null => {
  if (!email || !email.includes('@')) return null;
  return normalizeDomain(email.split('@').pop() ?? '');
};

const domainsMatch = (firstDomain: string, secondDomain: string): boolean => (
  firstDomain === secondDomain ||
  firstDomain.endsWith(`.${secondDomain}`) ||
  secondDomain.endsWith(`.${firstDomain}`)
);

/**
 * Check if a place (business) has any catch-all emails
 * Checks both company_emails and all people emails
 *
 * Used by enrich-people actions to determine if the business domain is catch-all.
 * If ANY email from the business is catch-all, the domain is catch-all,
 * so all newly discovered DM emails will also be catch-all.
 */
export const placeHasCatchAllEmails = (place: Place): boolean => {
  // Check company emails
  if (place.company_emails && hasCatchAllEmails(place.company_emails)) {
    return true;
  }

  // Check people emails
  if (place.people) {
    for (const person of place.people) {
      if (person.emails && hasCatchAllEmails(person.emails)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Check whether the business website domain itself is catch-all.
 * Public or unrelated mailbox domains found on the page should not block people enrichment.
 */
export const placeHasCatchAllBusinessDomain = (place: Place): boolean => {
  const websiteDomain = getWebsiteDomain(place.website);
  if (!websiteDomain) return false;

  const allEmails = [
    ...(place.company_emails ?? []),
    ...(place.people ?? []).flatMap(person => person.emails ?? []),
  ];

  return allEmails.some(email => {
    if (email.is_catch_all !== true) return false;
    const emailDomain = getEmailDomain(email.email);
    return emailDomain ? domainsMatch(websiteDomain, emailDomain) : false;
  });
};
